import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/table-core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DataTableViewOptions } from "@/components/DataTable/Table/DataTableViewOptions";
import Icon from "@/components/Icon/Icon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { defaultNS } from "@/lib/i18n";
import { ManualFormHash } from "@/routes/_staff/apps/custom-app/add/-constants/constant";

type Row = {
  /** シャドーITのアプリID */
  id: string;
  traceableApp: {
    id: string;
    integratableAppId?: string;
  };
};

export function ShadowItAppsActionsColumn<T extends Row>(): ColumnDef<T> {
  return {
    accessorKey: "actions",
    header: ({ table }) => (
      <div className="flex justify-end pr-2">
        <DataTableViewOptions table={table} />
      </div>
    ),
    cell: ({ row }) => (
      <ActionsCell
        id={row.original.id}
        traceableApp={row.original.traceableApp}
      />
    ),
  };
}

function ActionsCell<T extends Row>({ traceableApp }: T) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.apps.alert.shadowIt.apps",
  });

  const menu = useMemo(() => {
    return [
      ...(traceableApp.integratableAppId
        ? [
            {
              label: t("integrate"),
              href: "/apps/marketplace/$id" as const,
              params: { id: traceableApp.integratableAppId },
              search: {},
              hash: undefined,
              hidden: false,
            },
          ]
        : []),
      {
        label: t("createCustomApp"),
        href: "/apps/custom-app/add/$customAppType" as const,
        params: { customAppType: "CUSTOM_APP" as const },
        search: { traceableAppId: traceableApp.id },
        hash: ManualFormHash.BASIC,
      },
    ] as const;
  }, [t, traceableApp.integratableAppId, traceableApp.id]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <Icon iconName="MoreHorizontalIcon" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {menu.map((item) => (
          <DropdownMenuItem asChild key={item.label}>
            <Link
              to={item.href}
              params={item.params}
              search={item.search}
              hash={item.hash}
            >
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
